/**
 * memoryStorage — StoragePort adapter backed by an in-memory world.
 *
 * Used in dev / demo / offline mode. Any other storage adapter (MongoDB,
 * Postgres, Supabase) MUST expose the same surface.
 */

/** @returns {import('../../domain/ports').StoragePort} */
export function createMemoryStorage() {
  const state = {
    actors: [],
    signals: [],
    opportunities: [],
    plays: [],
    outcomes: [],
    closures: [],
    teamMembers: [],
  };

  const clone = (x) => JSON.parse(JSON.stringify(x));
  const wait = () => Promise.resolve();

  const port = {
    // ---- internal seeding helper (used by application/seed.js only) ----
    __replace(nextState) {
      state.actors = nextState.actors || [];
      state.signals = nextState.signals || [];
      state.opportunities = nextState.opportunities || [];
      state.plays = nextState.plays || [];
      state.outcomes = nextState.outcomes || [];
      state.closures = nextState.closures || [];
      state.teamMembers = nextState.teamMembers || [];
    },
    __dump() { return clone(state); },

    // ---- Actors ----
    async listActors(filter = {}) {
      await wait();
      let out = state.actors.slice();
      if (filter.vertical) out = out.filter((a) => a.vertical === filter.vertical);
      if (filter.stage) out = out.filter((a) => a.stage === filter.stage);
      return clone(out);
    },
    async getActor(id) {
      await wait();
      // clone(undefined) explota (JSON.parse("undefined") no es JSON
      // válido) antes de que "|| null" pueda aplicar — el find() debe
      // resolverse primero, nunca clonar un resultado inexistente.
      const found = state.actors.find((a) => a.id === id);
      return found ? clone(found) : null;
    },
    async saveActor(actor) {
      await wait();
      const i = state.actors.findIndex((a) => a.id === actor.id);
      if (i >= 0) state.actors[i] = actor;
      else state.actors.push(actor);
      return clone(actor);
    },

    // ---- Signals ----
    async listSignals(actorId) {
      await wait();
      return clone(state.signals.filter((s) => s.actorId === actorId));
    },
    async saveSignal(signal) {
      await wait();
      state.signals.push(signal);
      return clone(signal);
    },

    // ---- Opportunities ----
    async listOpportunities(filter = {}) {
      await wait();
      let out = state.opportunities.slice();
      if (filter.engineId) out = out.filter((o) => o.engineId === filter.engineId);
      if (filter.priority) out = out.filter((o) => o.priority === filter.priority);
      if (filter.outcome) out = out.filter((o) => o.outcome === filter.outcome);
      if (filter.actorId) out = out.filter((o) => o.actorId === filter.actorId);
      out.sort((a, b) => b.estimatedValue.value.amount - a.estimatedValue.value.amount);
      if (filter.limit) out = out.slice(0, filter.limit);
      return clone(out);
    },
    async getOpportunity(id) {
      await wait();
      const found = state.opportunities.find((o) => o.id === id);
      return found ? clone(found) : null;
    },
    async saveOpportunity(opp) {
      await wait();
      state.opportunities.push(opp);
      return clone(opp);
    },
    async updateOpportunity(id, patch) {
      await wait();
      const i = state.opportunities.findIndex((o) => o.id === id);
      if (i < 0) throw new Error("Opportunity not found");
      state.opportunities[i] = { ...state.opportunities[i], ...patch, updatedAt: new Date().toISOString() };
      return clone(state.opportunities[i]);
    },

    // ---- Plays ----
    async listPlays(opportunityId) {
      await wait();
      return clone(state.plays.filter((p) => p.opportunityId === opportunityId));
    },
    async getLatestPlay(opportunityId) {
      await wait();
      const list = state.plays.filter((p) => p.opportunityId === opportunityId);
      if (!list.length) return null;
      return clone(list[list.length - 1]);
    },
    async savePlay(play) {
      await wait();
      state.plays.push(play);
      return clone(play);
    },

    // ---- Outcomes ----
    async listOutcomes(opportunityId) {
      await wait();
      if (!opportunityId) return clone(state.outcomes);
      return clone(state.outcomes.filter((o) => o.opportunityId === opportunityId));
    },
    async saveOutcome(outcome) {
      await wait();
      state.outcomes.push(outcome);
      return clone(outcome);
    },

    // ---- Closures ----
    async listClosures(filter = {}) {
      await wait();
      let out = state.closures.slice().sort((a, b) => new Date(b.closedAt) - new Date(a.closedAt));
      if (filter.engineId) out = out.filter((c) => c.engineId === filter.engineId);
      if (filter.limit) out = out.slice(0, filter.limit);
      return clone(out);
    },
    async saveClosure(closure) {
      await wait();
      state.closures.unshift(closure);
      return clone(closure);
    },

    // ---- Team ----
    async listTeamMembers() {
      await wait();
      return clone(state.teamMembers);
    },
    async saveTeamMember(member) {
      await wait();
      const i = state.teamMembers.findIndex((m) => m.id === member.id);
      if (i >= 0) state.teamMembers[i] = member;
      else state.teamMembers.push(member);
      return clone(member);
    },
  };

  return port;
}
